import { FocusTrapFactory } from '@angular/cdk/a11y';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Type, inject } from '@angular/core';
import { DialogRef } from './dialog-ref';
import { DialogComponent } from './dialog-component';
import { DIALOG_DEFAULT_CONFIG, DialogConfig } from './dialog.config';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private overlay = inject(Overlay);
  private focusTrapFactory = inject(FocusTrapFactory);
  private defaultConfig = inject(DIALOG_DEFAULT_CONFIG);

  /**
   * Opens a component in a centered overlay
   * @param component Component to render in the overlay
   * @param config Optional configuration
   * @returns A DialogRef instance
   */
  open<T extends DialogComponent<D, R>, D = unknown, R = unknown>(component: Type<T>, config?: DialogConfig<D>): DialogRef<R, D> {
    // Create overlay config
    const overlayConfig = new OverlayConfig({
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      hasBackdrop: config?.hasBackdrop ?? this.defaultConfig.hasBackdrop,
      backdropClass: config?.backdropClass || this.defaultConfig.backdropClass,
      width: config?.width || this.defaultConfig.width,
      height: config?.height || this.defaultConfig.height,
      panelClass: config?.panelClass
    });

    // Create overlay
    const overlayRef = this.overlay.create(overlayConfig);

    // Create dialog reference
    const dialogRef = new DialogRef<R, D>(overlayRef);

    // Create and attach the component
    const componentRef = overlayRef.attach(new ComponentPortal(component));
    const componentInstance = componentRef.instance;

    // Set component instance on dialog reference
    dialogRef.setComponentInstance(componentInstance);

    // Create focus trap for accessibility
    const focusTrap = this.focusTrapFactory.create(overlayRef.overlayElement);
    focusTrap.focusInitialElement();

    // Pass data if provided
    if (config?.data) {
      componentRef.setInput('data', config.data);
    }

    // Subscribe to dialog close events
    componentInstance.dialogClose.subscribe(() => {
      dialogRef.close();
      focusTrap.destroy();
    });

    // Subscribe to dialog submit events - don't auto-close, just store the result
    componentInstance.dialogSubmit.subscribe((result: R) => {
      dialogRef.setResult(result);
    });

    // Handle overlay detachments
    overlayRef.detachments().subscribe(() => {
      focusTrap.destroy();
      // If dialog hasn't been closed yet, close it now
      if (!dialogRef.isClosed()) {
        dialogRef.close();
      }
    });

    return dialogRef;
  }

  /**
   * Closes a dialog with an optional result
   * @param dialogRef The dialog reference to close
   * @param result Optional result to pass back
   */
  close<R, D = unknown>(dialogRef: DialogRef<R, D>, result?: R): void {
    dialogRef.close(result);
  }
}
